/**
 * A vertical list of mobile cards, each an image overhanging a `tertiary` panel with a title and
 * description; the image is indented 20 px on every second card, which gives the even/odd
 * stagger. The title is always wrapped in a `Link`, to `actionUrl` or to `""` when there is none;
 * a card with both `actionUrl` and `title` also gets a label-less `ButtonLink` to `actionUrl`.
 *
 * Mobile only: `components/References/SubPages/SubPages.tsx` renders it below `lg` for the
 * Metal Resources references and tools-and-calculators index pages
 * (`pages/metal-resources/references/index.tsx`,
 * `pages/metal-resources/tools-calculators/index.tsx`).
 * The file name misspells "Even Odd"; the exports are spelt correctly.
 */
import React from "react";
import { Typography } from "@ryerson/frontend.typography";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { useTheme } from "@ryerson/frontend.theme";
import { ButtonLink, Link } from "@ryerson/frontend.navigation";

/** One card. */
export type EvenOddImageWithTextCard = {
	/** Heading, image alt text, and the React key, so titles must be unique within a list. */
	title: string;
	description: string;
	/** Not rendered: the button has no label. */
	actionLabel?: string;
	actionUrl?: string;
	imageUrl: string;
};

/** Props: the cards, in display order. */
export type EvenOddImageWithTextContent = {
	cards: EvenOddImageWithTextCard[];
};

const MobileCard = styled.div`
	width: 100%;
	display: block;
	padding-bottom: 45px;
	position: relative;
	margin-top: 60px;
	${(props: any) => {
		const { theme } = props;
		return css`
			background-color: ${theme.colors.tertiary.background};
		`;
	}}
`;

const MobileCardImage = styled.div`
	width: calc(100% - 20px);
	position: relative;
	top: -40px;
	margin-bottom: -40px;
`;

const MobileCardText = styled.div`
	width: 100%;
	padding-left: 20px;
	padding-right: 20px;
	box-sizing: border-box;
`;

/** Renders the staggered card list; the last card gets a 40 px bottom margin. */
const EvenOddImageWithText: React.FC<EvenOddImageWithTextContent> = ({ cards }) => {
	const { theme } = useTheme();

	return (
		<>
			{cards.map((card: EvenOddImageWithTextCard, index: number) => (
				<React.Fragment key={card.title}>
					<MobileCard
						theme={theme}
						css={css`
							margin-bottom: ${cards && index === cards.length - 1 ? "40px" : "0px"};
						`}
					>
						<MobileCardImage>
							<img
								css={css`
									width: 100%;
									height: auto;
									display: block;
									margin-left: ${index % 2 === 0 ? "0px" : "20px"};
								`}
								src={card.imageUrl}
								alt={card.title}
							/>
						</MobileCardImage>
						<MobileCardText>
							<Link to={card.actionUrl ?? ""}>
								<Typography
									variant="h3"
									type="tertiary"
									css={css`
										margin-top: 25px;
										margin-bottom: 25px;
									`}
								>
									{card.title}
								</Typography>
							</Link>
							<Typography type="tertiary" size="md">
								{card.description}
							</Typography>
						</MobileCardText>
						{card.actionUrl && card.title ? (
							<ButtonLink type="tertiary" label="" to={card.actionUrl} />
						) : (
							<></>
						)}
					</MobileCard>
				</React.Fragment>
			))}
		</>
	);
};

export default EvenOddImageWithText;
